<script>
	import { gameUrl, teamUrl } from './shared.js';

	export let playoffPairs = [];
	export let tag = '';
	export let compId = '';

	let openPairKey = '';
	let hoverPairKey = '';

	$: trees = buildTrees(playoffPairs);

	function toNumber(value, fallback = 0) {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	function pairKey(pair) {
		return [
			pair?.tree ?? 0,
			pair?.round ?? 0,
			pair?.sort ?? 0,
			pair?.comp?.id ?? pair?.comp?.Id ?? ''
		].join(':');
	}

	function isPlaceholderTeam(team) {
		if (!team) return true;
		const teamId = team.teamId ?? team.TeamId;
		if (teamId === null || teamId === undefined || teamId === '' || Number(teamId) === 0) {
			return true;
		}

		const logo = String(team.logo || '');
		return /\/logo\/0\.png(\?|$)/i.test(logo);
	}

	function hasUsableLogo(team) {
		if (isPlaceholderTeam(team) || !team?.logo) return false;
		return !/\/logo\/0\.png(\?|$)/i.test(String(team.logo));
	}

	function getTeamName(team) {
		if (!team) return 'TBD';
		return team.shortName || team.name || team.abcName || 'TBD';
	}

	function getTeamInitial(team) {
		const name = getTeamName(team);
		return (name.replace(/^команда\s+/i, '').trim()[0] || '?').toUpperCase();
	}

	function getTeamHref(team) {
		if (isPlaceholderTeam(team)) return '';
		const teamId = team?.teamId ?? team?.TeamId;
		if (!teamId) return '';
		return teamUrl(teamId, { tag, compId });
	}

	function getSeriesScore(pair, side) {
		const score = side === 1 ? pair?.score1 : pair?.score2;
		if (score === null || score === undefined || score === '') return '—';
		return String(score);
	}

	function getWinnerSide(pair) {
		return toNumber(pair?.winner, 0);
	}

	function getRoundLabel(round, pairs) {
		const names = [...new Set((pairs || []).map((pair) => pair?.comp?.name).filter(Boolean))];
		if (names.length === 1) return names[0];

		const labels = {
			16: '1/16 финала',
			8: '1/8 финала',
			4: '1/4 финала',
			2: '1/2 финала',
			1: 'Финал'
		};

		return labels[round] || (round ? `Раунд ${round}` : '');
	}

	function getGames(pair) {
		return Array.isArray(pair?.scores) ? pair.scores.filter((score) => score?.gameId) : [];
	}

	function getGameHref(score) {
		return gameUrl(score.gameId, { tag, compId });
	}

	function isPairOpen(pair) {
		const key = pairKey(pair);
		return openPairKey === key || hoverPairKey === key;
	}

	function togglePair(pair) {
		if (!getGames(pair).length) return;
		const key = pairKey(pair);
		openPairKey = openPairKey === key ? '' : key;
	}

	function closePair(pair) {
		const key = pairKey(pair);
		if (openPairKey === key) openPairKey = '';
		if (hoverPairKey === key) hoverPairKey = '';
	}

	function handlePairKeydown(event, pair) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			togglePair(pair);
		}
		if (event.key === 'Escape') {
			closePair(pair);
		}
	}

	function buildTrees(rawPairs) {
		const pairs = Array.isArray(rawPairs) ? rawPairs.filter(Boolean) : [];
		if (!pairs.length) return [];

		const treeIds = [...new Set(pairs.map((pair) => toNumber(pair.tree, 1)))].sort((a, b) => a - b);
		const roundIds = [...new Set(pairs.map((pair) => toNumber(pair.round, 0)).filter(Boolean))].sort((a, b) => b - a);

		const pairsByTreeRound = new Map();
		treeIds.forEach((treeId) => {
			roundIds.forEach((roundId) => {
				const roundPairs = pairs
					.filter((pair) => toNumber(pair.tree, 1) === treeId && toNumber(pair.round, 0) === roundId)
					.sort((a, b) => toNumber(a.sort, 0) - toNumber(b.sort, 0));
				pairsByTreeRound.set(`${treeId}:${roundId}`, roundPairs);
			});
		});

		// The left offset applies per column (round index), not per tree: if
		// any tree merges more than one pair into this column from the
		// previous round, every tree's round in this same column gets the
		// offset — even secondary trees like a 3rd-place match whose own
		// previous round is empty — so all cards/titles stay aligned.
		const incomingByIndex = roundIds.map((roundId, index) => {
			if (index === 0) return false;
			const previousRoundId = roundIds[index - 1];
			return treeIds.some((treeId) => pairsByTreeRound.get(`${treeId}:${previousRoundId}`).length > 1);
		});

		return treeIds.map((treeId) => {
			const rounds = roundIds.map((roundId, index) => {
				const roundPairs = pairsByTreeRound.get(`${treeId}:${roundId}`);
				// The connector line itself is only drawn when this specific
				// tree's own previous round actually merges more than one
				// pair — otherwise there is nothing real for it to connect to
				// (e.g. a 3rd-place match's own bracket has no earlier round).
				const previousOwnRoundPairs = index > 0 ? pairsByTreeRound.get(`${treeId}:${roundIds[index - 1]}`) : [];
				const showIncomingLine = previousOwnRoundPairs.length > 1;

				return {
					round: roundId,
					label: getRoundLabel(roundId, roundPairs),
					pairs: roundPairs,
					showConnectors: roundPairs.length > 1 && index < roundIds.length - 1,
					showIncoming: incomingByIndex[index],
					showIncomingLine
				};
			});

			return { tree: treeId, rounds };
		});
	}
</script>

{#if !trees.length}
	<div class="widget-status">Сетка плей-офф пока не сформирована</div>
{:else}
	<div class="playoff">
		{#each trees as tree (tree.tree)}
			<div class="playoff-tree" data-tree={tree.tree}>
				<div class="playoff-tree__inner">
					{#each tree.rounds as round (round.round)}
						<div
							class="playoff-round"
							class:--empty={!round.pairs.length}
							class:--connectors={round.showConnectors}
							class:--incoming={round.showIncoming}
							class:--incoming-line={round.showIncomingLine}
							style="--slots: {Math.max(round.pairs.length, 1)}"
						>
							<div class="playoff-round__title">
								{round.pairs.length ? round.label : '\u00a0'}
							</div>

							<div class="playoff-round__slots">
								{#if round.pairs.length}
									{#each round.pairs as pair (pairKey(pair))}
										{@const winner = getWinnerSide(pair)}
										{@const games = getGames(pair)}
										{@const open = isPairOpen(pair)}
										<div class="playoff-slot">
											<div
												class="pair"
												class:--current={pair.isCurrentRound}
												class:--done={pair.isFinal}
												class:--open={open}
												class:--has-games={games.length > 0}
												role="group"
												tabindex={games.length ? 0 : -1}
												aria-expanded={games.length ? open : undefined}
												on:mouseenter={() => { if (games.length) hoverPairKey = pairKey(pair); }}
												on:mouseleave={() => { hoverPairKey = ''; }}
												on:click={() => togglePair(pair)}
												on:keydown={(event) => handlePairKeydown(event, pair)}
											>
												<div class="pair__teams">
													{#each [1, 2] as side}
														{@const team = side === 1 ? pair.team1 : pair.team2}
														{@const href = getTeamHref(team)}
														{@const placeholder = isPlaceholderTeam(team)}
														{@const winnerSide = winner === side}
														{@const loserSide = winner === 1 || winner === 2 ? winner !== side : false}
														<svelte:element
															this={href ? 'a' : 'div'}
															class="pair__team"
															class:--winner={winnerSide}
															class:--loser={loserSide}
															class:--tbd={placeholder}
															href={href || undefined}
															on:click={(event) => { if (href) event.stopPropagation(); }}
														>
															{#if hasUsableLogo(team)}
																<img
																	class="pair__logo"
																	src={team.logo}
																	alt=""
																	on:error={(event) => event.currentTarget.remove()}
																>
															{:else}
																<span class="pair__logo-fallback">{getTeamInitial(team)}</span>
															{/if}
															<span class="pair__meta">
																<span class="pair__name">{getTeamName(team)}</span>
																{#if team?.regionName && !placeholder}
																	<span class="pair__region">{team.regionName}</span>
																{/if}
															</span>
															<span class="pair__score">{getSeriesScore(pair, side)}</span>
														</svelte:element>
													{/each}
												</div>

												{#if games.length}
													<div class="pair__hint">{games.length} {games.length === 1 ? 'игра' : games.length < 5 ? 'игры' : 'игр'}</div>
													<div class="pair__games" class:--visible={open}>
														<div class="pair__games-title">Матчи серии</div>
														{#each games as score (score.gameId)}
															<a
																class="pair__game"
																class:--forfeit={score.gameStatus === 'Forfeit'}
																href={getGameHref(score)}
																on:click={(event) => event.stopPropagation()}
															>
																<span class="pair__game-date">{score.gameDate || '—'}</span>
																<span class="pair__game-score">{score.score || '—'}</span>
																{#if score.gameStatus === 'Forfeit'}
																	<span class="pair__game-status">тех. пор.</span>
																{/if}
																<span class="pair__game-go" aria-hidden="true">→</span>
															</a>
														{/each}
													</div>
												{/if}
											</div>
										</div>
									{/each}
								{:else}
									<div class="playoff-slot --placeholder" aria-hidden="true"></div>
								{/if}
							</div>
						</div>
					{/each}
				</div>
			</div>
		{/each}
	</div>
{/if}

<style>
	.playoff {
		display: flex;
		flex-direction: column;
		gap: 28px;
		overflow-x: auto;
		padding-bottom: 8px;
		-webkit-overflow-scrolling: touch;
	}

	.playoff-tree__inner {
		display: flex;
		align-items: stretch;
		min-width: min-content;
		width: 100%;
	}

	.playoff-round {
		display: flex;
		flex-direction: column;
		flex: 1 0 250px;
		min-width: 250px;
	}

	.playoff-round.--empty {
		flex-grow: 1;
	}

	.playoff-round__title {
		padding-right: 28px;
		color: #00244d;
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 0.02em;
		text-transform: uppercase;
	}

	.playoff-round.--incoming .playoff-round__title {
		padding-left: 28px;
	}

	.playoff-round__slots {
		display: flex;
		flex-direction: column;
		flex: 1;
		justify-content: space-around;
		min-height: calc(var(--slots) * 92px);
	}

	.playoff-slot {
		position: relative;
		display: flex;
		flex: 1 1 auto;
		align-items: center;
		padding: 10px 28px 10px 0;
	}

	.playoff-round.--incoming .playoff-slot {
		padding-left: 28px;
	}

	.playoff-slot.--placeholder {
		min-height: 0;
		padding: 0;
	}

	/*
	 * Incoming stub: a short straight line in the left gutter, running from
	 * the round's left edge (where the previous round's bracket merges) to
	 * the middle of this round's card, so the bracket visually flows into it.
	 * Only drawn when this tree's own previous round actually merges more
	 * than one pair (--incoming-line) — an offset-only round (--incoming
	 * without the line, e.g. a 3rd-place match with no earlier round of its
	 * own) gets the aligning padding but no line to nowhere.
	 */
	.playoff-round.--incoming-line .playoff-slot:not(.--placeholder)::after {
		content: '';
		position: absolute;
		left: 0;
		top: 50%;
		width: 28px;
		border-top: 2px solid #d7dce2;
		pointer-events: none;
	}

	/*
	 * Connector between rounds: a single pseudo-element per slot, bent with a
	 * rounded corner. The top half of a pair curves down, the bottom half
	 * curves up, and both meet exactly at the shared edge between the two
	 * slots — this avoids the doubled/overlapping segments that made the
	 * previous two-pseudo-element version look jagged.
	 */
	.playoff-round.--connectors .playoff-slot::before {
		content: '';
		position: absolute;
		right: 0;
		width: 28px;
		border: 0 solid #d7dce2;
		pointer-events: none;
	}

	.playoff-round.--connectors .playoff-slot:nth-child(odd)::before {
		top: 50%;
		height: 50%;
		border-top-width: 2px;
		border-right-width: 2px;
		border-radius: 0 10px 0 0;
	}

	.playoff-round.--connectors .playoff-slot:nth-child(even)::before {
		bottom: 50%;
		height: 50%;
		border-bottom-width: 2px;
		border-right-width: 2px;
		border-radius: 0 0 10px 0;
	}

	/* A slot left without a partner (odd pair count) just passes straight through. */
	.playoff-round.--connectors .playoff-slot:nth-child(odd):last-child::before {
		top: 50%;
		height: 0;
		border-top-width: 2px;
		border-right-width: 0;
		border-radius: 0;
	}

	.pair {
		position: relative;
		width: 100%;
		padding: 8px;
		background: #fff;
		border: 1px solid #d7dce2;
		border-radius: 12px;
		box-shadow: 0 1px 2px rgba(0, 36, 77, 0.04);
		transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
	}

	.pair.--has-games {
		cursor: pointer;
	}

	.pair.--current {
		border-color: #00244d;
	}

	.pair.--open,
	.pair:hover,
	.pair:focus-visible {
		z-index: 3;
		border-color: #00244d;
		box-shadow: 0 8px 22px rgba(0, 36, 77, 0.14);
		transform: translateY(-1px);
		outline: none;
	}

	.pair__teams {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.pair__team {
		display: flex;
		align-items: center;
		gap: 8px;
		min-height: 36px;
		padding: 4px 6px;
		border-radius: 8px;
		color: inherit;
		text-decoration: none;
	}

	a.pair__team:hover .pair__name {
		text-decoration: underline;
	}

	.pair__team.--winner {
		background: #f7f9fc;
	}

	.pair__team.--winner .pair__name,
	.pair__team.--winner .pair__score {
		color: #00244d;
		font-weight: 700;
	}

	.pair__team.--loser {
		opacity: 0.62;
	}

	.pair__team.--tbd {
		opacity: 0.72;
	}

	.pair__logo,
	.pair__logo-fallback {
		width: 28px;
		height: 28px;
		flex: 0 0 28px;
		border-radius: 6px;
		object-fit: contain;
		background: #edf2f7;
	}

	.pair__logo-fallback {
		display: flex;
		align-items: center;
		justify-content: center;
		color: #00244d;
		font-size: 12px;
		font-weight: 700;
	}

	.pair__meta {
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		min-width: 0;
	}

	.pair__name {
		overflow: hidden;
		color: #122033;
		font-size: 13px;
		font-weight: 600;
		line-height: 1.2;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.pair__region {
		color: #6b7a90;
		font-size: 11px;
		line-height: 1.2;
	}

	.pair__score {
		flex: 0 0 22px;
		color: #122033;
		font-size: 14px;
		font-weight: 700;
		text-align: right;
	}

	.pair__hint {
		margin-top: 6px;
		color: #6b7a90;
		font-size: 11px;
		text-align: right;
	}

	.pair__games {
		display: none;
		position: absolute;
		left: 0;
		right: 0;
		top: calc(100% + 6px);
		z-index: 4;
		padding: 8px;
		background: #fff;
		border: 1px solid #d7dce2;
		border-radius: 10px;
		box-shadow: 0 10px 24px rgba(0, 36, 77, 0.16);
	}

	/*
	 * Invisible bridge covering the gap between the card and the popup below
	 * it. Without this, moving the mouse from the card down to the popup
	 * crosses a dead zone with no element under the cursor, which triggers
	 * mouseleave on .pair and closes the popup before the user can reach it.
	 * The bridge is a descendant of .pair__games (itself a descendant of
	 * .pair), so hovering it keeps the whole thing counted as "still hovering
	 * .pair".
	 */
	.pair__games::before {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		top: -6px;
		height: 6px;
	}

	.pair__games.--visible {
		display: block;
	}

	.pair__games-title {
		margin-bottom: 6px;
		color: #6b7a90;
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}

	.pair__game {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 7px 8px;
		border-radius: 8px;
		color: #122033;
		font-size: 13px;
		text-decoration: none;
	}

	.pair__game:hover {
		background: #f7f9fc;
	}

	.pair__game-date {
		flex: 0 0 42px;
		color: #53657b;
		font-weight: 600;
	}

	.pair__game-score {
		flex: 1 1 auto;
		font-weight: 700;
	}

	.pair__game-status {
		color: #b42318;
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
	}

	.pair__game-go {
		color: #00244d;
		font-weight: 700;
	}

	@media (hover: hover) and (pointer: fine) {
		.pair.--has-games:hover .pair__games,
		.pair.--has-games:focus-within .pair__games {
			display: block;
		}
	}

	@media (max-width: 700px) {
		.playoff {
			gap: 20px;
			margin: 0 -8px;
			padding: 0 8px 12px;
		}

		.playoff-round {
			flex-basis: 220px;
			min-width: 220px;
		}

		.playoff-round__title {
			padding-right: 18px;
			font-size: 12px;
		}

		.playoff-round.--incoming .playoff-round__title {
			padding-left: 18px;
		}

		.playoff-slot {
			padding-right: 18px;
		}

		.playoff-round.--incoming .playoff-slot {
			padding-left: 18px;
		}

		.playoff-round.--incoming .playoff-slot::after {
			width: 18px;
		}

		.playoff-round.--connectors .playoff-slot::before {
			width: 18px;
			border-radius: 0 6px 0 0;
		}

		.playoff-round.--connectors .playoff-slot:nth-child(even)::before {
			border-radius: 0 0 6px 0;
		}

		.playoff-round.--connectors .playoff-slot:nth-child(odd):last-child::before {
			border-radius: 0;
		}

		.pair__region {
			display: none;
		}

		.pair__games {
			position: static;
			margin-top: 8px;
			box-shadow: none;
		}
	}
</style>
